import React, {

  useCallback,

  useEffect,

  useMemo,

  useRef,

  useState,

} from 'react';



import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNoteContentCompact.css';



/**

 * Delay between the clinician's final edit and autosave.

 */

const AUTOSAVE_DELAY_MS = 900;



/**

 * Resolve ClinicalNoteMain.entityVersion from a note response.

 */

function resolveEntityVersion(response) {

  if (!response) {

    return null;

  }



  return (

    response.entityVersion ??

    response.note?.entityVersion ??

    response.clinicalNote?.entityVersion ??

    null

  );

}



/**

 * Clinical-content rows are uniquely identified by their reusable

 * content template.

 */

function getContentKey(row) {

  return String(

    row?.contentTemplateId ??

      row?.clinicalNoteContentId ??

      'unknown'

  );

}



/**

 * Convert nullable backend text into a controlled React value.

 */

function toInputValue(value) {

  if (

    value === null ||

    value === undefined

  ) {

    return '';

  }



  return String(value);

}



/**

 * Normalize ClinicalContentResponse into editable frontend fields.

 */

function normalizeContentRow(row) {

  return {

    ...row,



    draftContentText:

      toInputValue(

        row?.contentText

      ),



    draftPatientSpecificModified:

      Boolean(

        row?.patientSpecificModified

      ),

  };

}



/**

 * Sort content sections according to the backend display order.

 */

function sortContentRows(rows) {

  return [...rows].sort(

    (first, second) => {

      const firstOrder =

        Number.isFinite(

          Number(

            first?.displayOrder

          )

        )

          ? Number(

              first.displayOrder

            )

          : Number.MAX_SAFE_INTEGER;



      const secondOrder =

        Number.isFinite(

          Number(

            second?.displayOrder

          )

        )

          ? Number(

              second.displayOrder

            )

          : Number.MAX_SAFE_INTEGER;



      if (

        firstOrder !==

        secondOrder

      ) {

        return (

          firstOrder -

          secondOrder

        );

      }



      return String(

        first?.displayNameSnapshot ??

          first?.contentCodeSnapshot ??

          ''

      ).localeCompare(

        String(

          second?.displayNameSnapshot ??

            second?.contentCodeSnapshot ??

            ''

        )

      );

    }

  );

}



/**

 * Content may remain incomplete while the note is still in DRAFT.

 *

 * Required-field enforcement belongs to full clinical-note

 * validation before review/signing.

 */

function validateContentRow(row) {

  const errors = {};



  if (

    row.draftPatientSpecificModified &&

    !row.allowPatientSpecificEdit

  ) {

    errors.patientSpecificModified =

      'Patient-specific modification is not allowed for this section.';

  }



  return errors;

}



/**

 * Merge a successful ClinicalContentResponse with the current local

 * draft.

 *

 * If the clinician changed the row while the request was in flight,

 * preserve the newer draft instead of replacing it with an older

 * server response.

 */

function mergeSavedContentRow(

  currentRow,

  savedResponse,

  preserveDraft

) {

  const normalized =

    normalizeContentRow(

      savedResponse

    );



  if (!preserveDraft) {

    return normalized;

  }



  return {

    ...normalized,



    draftContentText:

      currentRow

        .draftContentText,



    draftPatientSpecificModified:

      currentRow

        .draftPatientSpecificModified,

  };

}



/**

 * Previous clinical-note content display.

 *

 * Previous text is presented for comparison only.

 * The backend does not currently persist an explicit copy-forward

 * action for ClinicalNoteContentMain.

 */

function PreviousContent({

  row,

}) {

  const hasPrevious =

    Boolean(

      row.previousContentText

    );



  return (

    <div

      style={{

        border:

          '1px solid #e0e3e7',

        borderRadius: 7,

        background:

          '#fafafa',

        padding: 10,

      }}

    >

      <div

        style={{

          display: 'flex',

          justifyContent:

            'space-between',

          alignItems: 'center',

          gap: 8,

          flexWrap: 'wrap',

          marginBottom: 5,

        }}

      >

        <div

          style={{

            fontSize: 11,

            fontWeight: 700,

            textTransform:

              'uppercase',

            color: '#777',

          }}

        >

          Previous Visit

        </div>



        {row.previousClinicalNoteId && (

          <div

            style={{

              fontSize: 11,

              color: '#777',

            }}

          >

            Note #{row.previousClinicalNoteId}

          </div>

        )}

      </div>



      {!hasPrevious && (

        <div

          style={{

            fontSize: 12,

            color: '#777',

          }}

        >

          No previous narrative is

          available for this section.

        </div>

      )}



      {hasPrevious && (

        <div

          style={{

            fontSize: 13,

            lineHeight: 1.5,

            whiteSpace:

              'pre-wrap',

          }}

        >

          {row.previousContentText}

        </div>

      )}

    </div>

  );

}



/**

 * One narrative/content editor card.

 */

function ContentRow({

  row,

  disabled,

  saveStatus,

  saveError,

  validationErrors,

  onContentChange,

  onRetry,
  isDailyNote = false,

}) {

  const key =

    getContentKey(row);

  // Keep each editor mounted while its display is minimized.
  const [collapsed, setCollapsed] = useState(
    !/SUBJECTIVE/i.test(`${row?.contentCodeSnapshot || ''} ${row?.displayNameSnapshot || ''}`)
  );
  const [expandedEditor, setExpandedEditor] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const isSubjective = /SUBJECTIVE/i.test(
    `${row?.contentCodeSnapshot || ''} ${row?.displayNameSnapshot || ''}`
  );
  const subjectiveSuggestion =
    'Patient reports compliance with therapeutic exercise.';




  /*
   * Template-controlled wording remains visible in the clinical note,
   * but it may not be changed for an individual patient unless the
   * reusable template explicitly allows patient-specific editing.
   */
  const templateLocked =

    !row?.allowPatientSpecificEdit;
  const canOfferSuggestion =
    isDailyNote && isSubjective && !templateLocked &&
    !String(row?.draftContentText || '').trim();

  const hasRequiredText =
    Boolean(
      row?.draftContentText &&
      String(row.draftContentText).trim()
    ) &&
    Object.keys(validationErrors || {}).length === 0;

  const usingDefaultWording =
    hasRequiredText &&
    !row?.draftPatientSpecificModified;

  const requiredState =
    !row?.required
      ? 'optional'
      : !hasRequiredText
        ? 'red'
        : usingDefaultWording
          ? 'yellow'
          : 'green';



  let saveStatusText =

    disabled

      ? 'Read only'

      : templateLocked

        ? 'Template controlled'

        : 'Saved';



  switch (saveStatus) {

    case 'saving':

      saveStatusText =

        'Saving…';

      break;



    case 'unsaved':

      saveStatusText =

        'Unsaved';

      break;



    case 'invalid':

      saveStatusText =

        'Needs correction';

      break;



    case 'error':

      saveStatusText =

        'Autosave failed';

      break;



    default:

      break;

  }



  return (

    <article

      style={{

        border:

          validationErrors

            ?.patientSpecificModified

            ? '1px solid #b42318'

            : '1px solid #ddd',



        borderRadius: 10,

        background: '#fff',

        overflow: 'hidden',

      }}

    >

      {/* ===================================================

          HEADER

          =================================================== */}

      <div

        style={{

          display: 'flex',

          justifyContent:

            'space-between',

          alignItems:

            'flex-start',

          gap: 12,

          flexWrap: 'wrap',

          padding: 13,

          borderBottom:

            '1px solid #e5e5e5',

          background:

            '#fafafa',

        }}

      >

        <div>

          <div

            style={{

              display: 'flex',

              alignItems: 'center',

              gap: 7,

              flexWrap: 'wrap',

            }}

          >

            <strong

              style={{

                fontSize: 15,

              }}

            >

              {row.displayNameSnapshot ||

                row.contentCodeSnapshot ||

                'Clinical Narrative'}

            </strong>



            {row.required && (

              <span
                title={
                  requiredState === 'green'
                    ? 'Required clinical content is complete.'
                    : requiredState === 'yellow'
                      ? 'Using approved default/template wording. This is passable but should be reviewed.'
                      : 'Required clinical content is incomplete.'
                }
                style={{
                  borderRadius: 999,
                  padding: '2px 7px',
                  background:
                    requiredState === 'green'
                      ? '#e8f5e9'
                      : requiredState === 'yellow'
                        ? '#fff3cd'
                        : '#fce8e6',
                  color:
                    requiredState === 'green'
                      ? '#1f6b2a'
                      : requiredState === 'yellow'
                        ? '#795500'
                        : '#8a1c13',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {requiredState === 'green'
                  ? 'Required ✓'
                  : requiredState === 'yellow'
                    ? 'Default wording ⚠'
                    : 'Required'}
              </span>

            )}



            {row.allowPatientSpecificEdit ? (

              <span

                style={{

                  borderRadius: 999,

                  padding:

                    '2px 7px',

                  background:

                    '#eef3f8',

                  color: '#334e68',

                  fontSize: 11,

                  fontWeight: 700,

                }}

              >

                Patient-specific editing allowed

              </span>

            ) : (

              <span

                style={{

                  borderRadius: 999,

                  padding:

                    '2px 7px',

                  background:

                    '#f1f3f5',

                  color: '#495057',

                  fontSize: 11,

                  fontWeight: 700,

                }}

              >

                Template-controlled wording

              </span>

            )}



            {row.draftPatientSpecificModified && (

              <span

                style={{

                  borderRadius: 999,

                  padding:

                    '2px 7px',

                  background:

                    '#fff3cd',

                  color: '#795500',

                  fontSize: 11,

                  fontWeight: 700,

                }}

              >

                Patient-specific wording

              </span>

            )}

          </div>



          {row.contentCodeSnapshot && (

            <div

              style={{

                marginTop: 4,

                fontSize: 12,

                color: '#666',

              }}

            >

              {row.contentCodeSnapshot}

            </div>

          )}

        </div>



        <div

          aria-live="polite"

          style={{

            fontSize: 12,

            fontWeight: 700,

            color:

              saveStatus ===

              'error'

                ? '#b42318'

                : saveStatus ===

                    'invalid'

                  ? '#9a6700'

                  : saveStatus ===

                      'saving'

                    ? '#555'

                    : '#2e6a35',

          }}

        >

          {saveStatusText}

        </div>

      </div>



      <div className="clinical-note-narrative-actions">
        <button type="button" onClick={() => setCollapsed((current) => !current)}
          aria-expanded={!collapsed} aria-controls={`clinical-content-body-${key}`}>
          {collapsed ? 'Edit / View' : 'Minimize'}
        </button>
        {!collapsed && (
          <button type="button" onClick={() => setExpandedEditor((current) => !current)}>
            {expandedEditor ? 'Normal Size' : 'Expand Editor'}
          </button>
        )}
        {row.previousContentText && (
          <button type="button" onClick={() => { setCollapsed(false); setShowPrevious((current) => !current); }}
            aria-expanded={showPrevious}>
            {showPrevious ? 'Hide Previous' : 'Previous Visit'}
          </button>
        )}
      </div>
      {collapsed && (
        <div className="clinical-note-narrative-summary">
          {String(row.draftContentText || '').trim() ||
            (canOfferSuggestion ? `Suggested: ${subjectiveSuggestion}` : 'No documentation entered')}
          {saveError && <span className="clinical-note-narrative-error" role="alert">Autosave failed — expand to retry</span>}
        </div>
      )}
      <div id={`clinical-content-body-${key}`} aria-hidden={collapsed}
        style={{ display: collapsed ? 'none' : 'block', padding: 12 }}>


        {/* =================================================

            CURRENT NARRATIVE

            ================================================= */}

        <div>

          <label

            htmlFor={`clinical-content-${key}`}

            style={{

              display: 'block',

              marginBottom: 5,

              fontSize: 13,

              fontWeight: 700,

            }}

          >

            Current Documentation



            {row.required && (

              <span

                aria-hidden="true"

                style={{

                  marginLeft: 4,

                  color: '#b42318',

                }}

              >

                *

              </span>

            )}

          </label>



          {canOfferSuggestion && (
            <div className="clinical-note-narrative-suggestion">
              <span>Suggested text (not saved): {subjectiveSuggestion}</span>
              <button type="button" onClick={() => onContentChange(subjectiveSuggestion)}>
                Use Suggestion
              </button>
            </div>
          )}
          <textarea

            id={`clinical-content-${key}`}

            rows={expandedEditor ? 16 : 4}

            disabled={disabled}

            readOnly={templateLocked}

            value={

              row.draftContentText

            }

            onChange={(event) =>

              onContentChange(

                event.target.value

              )

            }

            placeholder={canOfferSuggestion ? subjectiveSuggestion : `Enter ${

              row.displayNameSnapshot ||

              'clinical documentation'

            }`}

            style={{

              width: '100%',

              boxSizing:

                'border-box',

              resize: 'vertical',

              minHeight: expandedEditor ? 320 : 88,

              border:

                validationErrors

                  ?.patientSpecificModified

                  ? '1px solid #b42318'

                  : '1px solid #bbb',

              borderRadius: 6,

              padding: 10,

              fontSize: 14,

              lineHeight: 1.5,

              background:

                disabled || templateLocked

                  ? '#f3f4f6'

                  : '#fff',

            }}

          />



          {row.allowPatientSpecificEdit ? (

            <div

              style={{

                marginTop: 5,

                fontSize: 11,

                color: '#666',

              }}

            >

              Editing this section for

              this patient is permitted

              by the configured

              template.

            </div>

          ) : (

            <div

              style={{

                marginTop: 5,

                fontSize: 11,

                color: '#666',

              }}

            >

              This wording comes from the

              approved clinical template and

              cannot be changed for this

              individual patient.

            </div>

          )}



          {validationErrors

            ?.patientSpecificModified && (

            <div

              role="alert"

              style={{

                marginTop: 5,

                fontSize: 12,

                color: '#b42318',

              }}

            >

              {

                validationErrors

                  .patientSpecificModified

              }

            </div>

          )}

        </div>



        {/* =================================================

            PREVIOUS NOTE

            ================================================= */}

        {showPrevious && <div

          style={{

            marginTop: 10,

          }}

        >

          <PreviousContent

            row={row}

          />

        </div>}



        {/* =================================================

            COPY-FORWARD INFORMATION

            ================================================= */}

        {showPrevious && row.previousContentText && (

          <div

            style={{

              marginTop: 8,

              fontSize: 11,

              color: '#666',

              lineHeight: 1.4,

            }}

          >

            Previous documentation is

            displayed for clinical

            comparison only. It is not

            automatically copied into

            the current note.

          </div>

        )}



        {/* =================================================

            SAVE ERROR

            ================================================= */}

        {saveError && (

          <div

            role="alert"

            style={{

              marginTop: 12,

              border:

                '1px solid #b42318',

              borderRadius: 8,

              background:

                '#fff5f5',

              padding: 11,

            }}

          >

            <strong>

              Clinical narrative was

              not saved.

            </strong>



            <div

              style={{

                marginTop: 4,

                fontSize: 13,

              }}

            >

              {saveError}

            </div>



            {!disabled && (

              <button

                type="button"

                onClick={onRetry}

                style={{

                  marginTop: 8,

                }}

              >

                Retry Save

              </button>

            )}

          </div>

        )}

      </div>

    </article>

  );

}



/**

 * Clinical Content / Narrative editor.

 *

 * Features:

 *

 * - configurable general and body-part-specific narrative sections

 * - required-section indicators

 * - current documentation

 * - previous-note comparison

 * - patient-specific modification tracking

 * - template-controlled wording is read only when editing is prohibited

 * - no false copy-forward claims

 * - serialized autosave

 * - optimistic-lock version refresh

 * - read-only support

 */

export default function ClinicalNoteContentSection({

  clinicalNoteId,

  disabled = false,

  onSaved,

  onVersionChange,

  navigationCoordinator,

}) {

  const [

    rows,

    setRows,

  ] = useState([]);

  const [loadedNoteType, setLoadedNoteType] = useState(null);



  const [

    loading,

    setLoading,

  ] = useState(true);



  const [

    loadError,

    setLoadError,

  ] = useState('');



  const [

    rowStatuses,

    setRowStatuses,

  ] = useState({});



  const [

    rowSaveErrors,

    setRowSaveErrors,

  ] = useState({});



  const [

    editTick,

    setEditTick,

  ] = useState(0);



  const [

    initialized,

    setInitialized,

  ] = useState(false);



  /*

   * Mutable autosave state.

   */

  const rowsRef =

    useRef([]);



  const versionRef =

    useRef(null);



  const rowRevisionRef =

    useRef({});



  const dirtyKeysRef =

    useRef(new Set());



  const failedKeysRef =

    useRef(new Set());



  const savingRef =

    useRef(false);



  const mountedRef =

    useRef(true);



  const flushSaveRef =

    useRef(null);

  // Navigation tracks unsaved revisions independently of the active queue.
  // A key remains here if its save fails or validation prevents saving.
  const unsavedKeysRef = useRef(new Set());
  const inFlightSaveRef = useRef(null);
  const initializedRef = useRef(false);
  const loadErrorRef = useRef('');



  useEffect(() => {

    rowsRef.current =

      rows;

  }, [rows]);



  useEffect(() => {

    return () => {

      mountedRef.current =

        false;

    };

  }, []);



  /**

   * Load:

   *

   * - configured/saved narrative sections

   * - current parent note version

   */

  useEffect(() => {

    let cancelled = false;



    async function loadContent() {

      setLoading(true);

      setLoadError('');
      loadErrorRef.current = '';
      initializedRef.current = false;

      setInitialized(false);

      setRowStatuses({});

      setRowSaveErrors({});



      dirtyKeysRef.current =

        new Set();

      unsavedKeysRef.current = new Set();



      failedKeysRef.current =

        new Set();



      rowRevisionRef.current =

        {};



      savingRef.current =

        false;



      try {

        const [

          contentResponse,

          noteResponse,

        ] =

          await Promise.all([

            clinicalNoteService

              .getContent(

                clinicalNoteId

              ),



            clinicalNoteService

              .getClinicalNote(

                clinicalNoteId

              ),

          ]);



        if (cancelled) {

          return;

        }



        const version =

          resolveEntityVersion(

            noteResponse

          );



        if (

          version === null ||

          version === undefined

        ) {

          throw new Error(

            'The clinical note version could not be loaded.'

          );

        }



        versionRef.current =

          version;

        setLoadedNoteType(noteResponse?.noteType ?? noteResponse?.note?.noteType ??
          noteResponse?.clinicalNote?.noteType ?? null);



        const normalizedRows =

          sortContentRows(

            (

              contentResponse ||

              []

            ).map(

              normalizeContentRow

            )

          );



        const statuses =

          {};



        const revisions =

          {};



        normalizedRows.forEach(

          (row) => {

            const key =

              getContentKey(

                row

              );



            statuses[key] =

              'saved';



            revisions[key] =

              0;

          }

        );



        rowsRef.current =

          normalizedRows;



        rowRevisionRef.current =

          revisions;



        setRows(

          normalizedRows

        );



        setRowStatuses(

          statuses

        );



        initializedRef.current = true;
        setInitialized(true);

      } catch (error) {

        if (cancelled) {

          return;

        }



        loadErrorRef.current = error?.message ||
          'Clinical narrative sections could not be loaded.';
        setLoadError(loadErrorRef.current);

      } finally {

        if (!cancelled) {

          setLoading(false);

        }

      }

    }



    if (clinicalNoteId) {

      loadContent();

    } else {

      setLoadError(

        'Clinical note ID is required.'

      );



      setLoading(false);

    }



    return () => {

      cancelled = true;

    };

  }, [clinicalNoteId]);



  /**

   * Refresh the parent optimistic-lock version after one narrative

   * child row is saved.

   */

  const refreshEntityVersion =

    useCallback(

      async () => {

        const noteResponse =

          await clinicalNoteService

            .getClinicalNote(

              clinicalNoteId

            );



        const newVersion =

          resolveEntityVersion(

            noteResponse

          );



        if (

          newVersion === null ||

          newVersion === undefined

        ) {

          throw new Error(

            'The clinical narrative was saved, but the updated clinical note version could not be loaded.'

          );

        }



        versionRef.current =

          newVersion;



        if (

          typeof onVersionChange ===

          'function'

        ) {

          onVersionChange(

            newVersion,

            noteResponse

          );

        }



        return noteResponse;

      },

      [

        clinicalNoteId,

        onVersionChange,

      ]

    );



  /**

   * Update the current narrative text.

   *

   * When the reusable template explicitly permits patient-specific

   * editing, editing the current narrative marks the row as

   * patient-specific modified.

   *

   * If the template does not permit that flag, the frontend keeps it

   * false so it remains compatible with the backend validation rule.

   */

  function updateContentText(

    key,

    value

  ) {

    if (disabled) {

      return;

    }



    const nextRows =

      rowsRef.current.map(

        (row) => {

          if (

            getContentKey(

              row

            ) !== key

          ) {

            return row;

          }



          return {

            ...row,



            draftContentText:

              value,



            draftPatientSpecificModified:

              row.allowPatientSpecificEdit

                ? true

                : false,

          };

        }

      );



    rowsRef.current =

      nextRows;



    setRows(

      nextRows

    );



    rowRevisionRef.current = {

      ...rowRevisionRef.current,



      [key]:

        (

          rowRevisionRef

            .current[key] ??

          0

        ) + 1,

    };



    failedKeysRef.current.delete(

      key

    );



    dirtyKeysRef.current.add(

      key

    );

    unsavedKeysRef.current.add(key);



    setRowSaveErrors(

      (current) => ({

        ...current,

        [key]: '',

      })

    );



    const editedRow =

      nextRows.find(

        (row) =>

          getContentKey(

            row

          ) === key

      );



    const errors =

      editedRow

        ? validateContentRow(

            editedRow

          )

        : {};



    setRowStatuses(

      (current) => ({

        ...current,



        [key]:

          Object.keys(

            errors

          ).length > 0

            ? 'invalid'

            : 'unsaved',

      })

    );



    setEditTick(

      (current) =>

        current + 1

    );

  }



  /**

   * Merge a successful backend response into the current row.

   */

  function applySuccessfulSave(

    key,

    response,

    revisionBeingSaved

  ) {

    const currentRevision =

      rowRevisionRef.current[

        key

      ] ?? 0;



    const preserveDraft =

      currentRevision !==

      revisionBeingSaved;



    const nextRows =

      rowsRef.current.map(

        (row) => {

          if (

            getContentKey(

              row

            ) !== key

          ) {

            return row;

          }



          return mergeSavedContentRow(

            row,

            response,

            preserveDraft

          );

        }

      );



    rowsRef.current =

      sortContentRows(

        nextRows

      );



    setRows(

      rowsRef.current

    );



    if (!preserveDraft) unsavedKeysRef.current.delete(key);

    if (preserveDraft) {

      dirtyKeysRef.current.add(

        key

      );



      setRowStatuses(

        (current) => ({

          ...current,

          [key]:

            'unsaved',

        })

      );

    } else {

      setRowStatuses(

        (current) => ({

          ...current,

          [key]:

            'saved',

        })

      );

    }

  }



  /**

   * Save dirty narrative sections sequentially.

   *

   * Each section autosave updates ClinicalNoteMain.entityVersion,

   * therefore child rows must not save concurrently using the same

   * expectedVersion.

   */

  const flushSaveQueue =

    useCallback(

      async () => {

        if (

          disabled ||

          !initialized ||

          savingRef.current

        ) {

          return;

        }



        savingRef.current =

          true;



        try {

          while (

            mountedRef.current &&

            dirtyKeysRef.current

              .size > 0

          ) {

            const key =

              Array.from(

                dirtyKeysRef.current

              ).find(

                (candidate) =>

                  !failedKeysRef

                    .current

                    .has(candidate)

              );



            if (!key) {

              break;

            }



            /*

             * Remove while this request is in flight.

             *

             * A new user edit will automatically add the row again.

             */

            dirtyKeysRef.current.delete(

              key

            );



            const row =

              rowsRef.current.find(

                (candidate) =>

                  getContentKey(

                    candidate

                  ) === key

              );



            if (!row) {

              continue;

            }



            const errors =

              validateContentRow(

                row

              );



            if (

              Object.keys(

                errors

              ).length > 0

            ) {

              setRowStatuses(

                (current) => ({

                  ...current,

                  [key]:

                    'invalid',

                })

              );



              continue;

            }



            const revisionBeingSaved =

              rowRevisionRef.current[

                key

              ] ?? 0;



            setRowStatuses(

              (current) => ({

                ...current,

                [key]:

                  'saving',

              })

            );



            setRowSaveErrors(

              (current) => ({

                ...current,

                [key]: '',

              })

            );



            try {

              if (

                versionRef.current ===

                  null ||

                versionRef.current ===

                  undefined

              ) {

                await refreshEntityVersion();

              }



              const response =

                await clinicalNoteService

                  .saveContent(

                    clinicalNoteId,

                    {

                      expectedVersion:

                        versionRef.current,



                      contentTemplateId:

                        row

                          .contentTemplateId,



                      contentText:

                        row

                          .draftContentText,



                      patientSpecificModified:

                        row

                          .draftPatientSpecificModified,

                    }

                  );



              if (

                !mountedRef.current

              ) {

                return;

              }



              /*

               * Refresh the parent note before allowing another child

               * section to save.

               */

              const noteResponse =

                await refreshEntityVersion();



              if (

                !mountedRef.current

              ) {

                return;

              }



              applySuccessfulSave(

                key,

                response,

                revisionBeingSaved

              );



              if (

                typeof onSaved ===

                'function'

              ) {

                onSaved(

                  response,

                  noteResponse

                );

              }

            } catch (error) {

              if (

                !mountedRef.current

              ) {

                return;

              }



              /*

               * Prevent repeated automatic failures.

               *

               * Another edit or Retry Save allows another attempt.

               */

              failedKeysRef.current.add(

                key

              );



              dirtyKeysRef.current.delete(

                key

              );



              setRowStatuses(

                (current) => ({

                  ...current,

                  [key]:

                    'error',

                })

              );



              setRowSaveErrors(

                (current) => ({

                  ...current,



                  [key]:

                    error?.message ||

                    'Clinical narrative could not be autosaved.',

                })

              );

            }

          }

        } finally {

          savingRef.current =

            false;



          const hasRetryableDirtyRows =

            Array.from(

              dirtyKeysRef.current

            ).some(

              (key) =>

                !failedKeysRef

                  .current

                  .has(key)

            );



          if (

            mountedRef.current &&

            hasRetryableDirtyRows

          ) {

            window.setTimeout(

              () => {

                flushSaveRef

                  .current?.();

              },

              150

            );

          }

        }

      },

      [

        clinicalNoteId,

        disabled,

        initialized,

        onSaved,

        refreshEntityVersion,

      ]

    );



  /**
   * Both normal autosave and navigation join the same in-flight promise.
   * Only clear the tracked promise when its own save has settled.
   */
  useEffect(() => {
    flushSaveRef.current = () => {
      if (inFlightSaveRef.current) return inFlightSaveRef.current;
      const running = flushSaveQueue();
      const tracked = Promise.resolve(running).finally(() => {
        if (inFlightSaveRef.current === tracked) {
          inFlightSaveRef.current = null;
        }
      });
      inFlightSaveRef.current = tracked;
      return tracked;
    };
  }, [flushSaveQueue]);

  /**
   * Register a safe visit-navigation barrier for this clinical section.
   * Drafts may be incomplete; only pending edits, invalid edits, failed
   * saves, or an unfinished load prevent switching visits.
   */
  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'content', {
      flush: async () => {
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
        if (dirtyKeysRef.current.size > 0) {
          await flushSaveRef.current?.();
        }
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
      },
      getState: () => {
        const unsaved = [...unsavedKeysRef.current];
        const invalid = unsaved.some((key) => {
          const row = rowsRef.current.find(item => getContentKey(item) === key);
          return row && Object.keys(validateContentRow(row)).length > 0;
        });
        const failed = unsaved.some(key => failedKeysRef.current.has(key));
        return {
          dirty: unsaved.length > 0 || !initializedRef.current,
          saving: Boolean(inFlightSaveRef.current),
          invalid,
          error: loadErrorRef.current ||
            (!initializedRef.current ? 'Clinical Content is still loading.' : '') ||
            (failed ? 'A clinical narrative failed to save. Correct it or use Retry Save before switching visits.' : ''),
        };
      },
    });
  }, [navigationCoordinator, clinicalNoteId]);



  /**

   * Debounced autosave.

   */

  useEffect(() => {

    if (

      !initialized ||

      disabled ||

      dirtyKeysRef.current

        .size === 0

    ) {

      return undefined;

    }



    const timer =

      window.setTimeout(

        () => {

          flushSaveRef

            .current?.();

        },

        AUTOSAVE_DELAY_MS

      );



    return () => {

      window.clearTimeout(

        timer

      );

    };

  }, [

    editTick,

    initialized,

    disabled,

  ]);



  /**

   * Retry one failed content save.

   */

  function retryRow(key) {

    if (disabled) {

      return;

    }



    failedKeysRef.current.delete(

      key

    );



    dirtyKeysRef.current.add(

      key

    );



    setRowSaveErrors(

      (current) => ({

        ...current,

        [key]: '',

      })

    );



    setRowStatuses(

      (current) => ({

        ...current,

        [key]:

          'unsaved',

      })

    );



    setEditTick(

      (current) =>

        current + 1

    );

  }



  /**

   * Per-row structural validation.

   */

  const validationByKey =

    useMemo(() => {

      const result = {};



      rows.forEach(

        (row) => {

          result[

            getContentKey(

              row

            )

          ] =

            validateContentRow(

              row

            );

        }

      );



      return result;

    }, [rows]);



  /**

   * Overall section autosave state.

   */

  const overallStatus =

    useMemo(() => {

      const values =

        Object.values(

          rowStatuses

        );



      if (

        values.includes(

          'saving'

        )

      ) {

        return 'Saving…';

      }



      if (

        values.includes(

          'error'

        )

      ) {

        return 'Autosave error';

      }



      if (

        values.includes(

          'invalid'

        )

      ) {

        return 'Needs correction';

      }



      if (

        values.includes(

          'unsaved'

        )

      ) {

        return 'Unsaved changes';

      }



      return disabled

        ? 'Read only'

        : 'Saved';

    }, [

      rowStatuses,

      disabled,

    ]);



  /**

   * Helpful draft-only count.

   *

   * This does not replace backend validation.

   */

  const incompleteRequiredCount =

    useMemo(

      () =>

        rows.filter(

          (row) =>

            row.required &&

            !row

              .draftContentText

              ?.trim()

        ).length,

      [rows]

    );



  if (loading) {

    return (

      <section

        style={{

          border:

            '1px solid #ddd',

          borderRadius: 10,

          padding: 16,

          background: '#fff',

        }}

      >

        Loading clinical narrative…

      </section>

    );

  }



  if (loadError) {

    return (

      <section

        style={{

          border:

            '1px solid #c62828',

          borderRadius: 10,

          padding: 16,

          background:

            '#fff5f5',

        }}

      >

        <strong>

          Clinical Content section

          could not be loaded

        </strong>



        <p

          style={{

            marginBottom: 0,

          }}

        >

          {loadError}

        </p>

      </section>

    );

  }



  return (

    <section

      style={{

        border:

          '1px solid #d7d7d7',

        borderRadius: 10,

        background: '#fff',

        overflow: 'hidden',

      }}

    >

      {/* =====================================================

          SECTION HEADER

          ===================================================== */}

      <div

        style={{

          display: 'flex',

          justifyContent:

            'space-between',

          alignItems: 'center',

          gap: 12,

          flexWrap: 'wrap',

          padding:

            '13px 16px',

          background:

            '#f7f8fa',

          borderBottom:

            '1px solid #ddd',

        }}

      >

        <div>

          <h3

            style={{

              margin: 0,

              fontSize: 17,

            }}

          >

            Clinical Content

          </h3>



          <div

            style={{

              marginTop: 3,

              fontSize: 12,

              color: '#666',

            }}

          >

            Narrative documentation for

            the patient's current

            clinical note.

          </div>

        </div>



        <div

          style={{

            textAlign: 'right',

          }}

        >

          <div

            aria-live="polite"

            style={{

              fontSize: 13,

              fontWeight: 700,

            }}

          >

            {overallStatus}

          </div>



          {incompleteRequiredCount >

            0 && (

            <div

              style={{

                marginTop: 3,

                fontSize: 12,

                color: '#9a6700',

                fontWeight: 700,

              }}

            >

              {incompleteRequiredCount}{' '}

              required{' '}

              {incompleteRequiredCount ===

              1

                ? 'section'

                : 'sections'}{' '}

              incomplete

            </div>

          )}

        </div>

      </div>



      <div

        style={{

          padding: 16,

        }}

      >

        <details className="clinical-note-narrative-help">
          <summary>About clinical narrative documentation</summary>
          <p>Sections come from the configured clinical templates. Required fields may
          remain incomplete in Draft; final validation is performed before signing.
          Previous-visit text is for comparison only and is never automatically copied.</p>
        </details>

        {/* ===================================================

            EMPTY STATE

            =================================================== */}

        {rows.length === 0 && (

          <div

            style={{

              border:

                '1px dashed #bbb',

              borderRadius: 8,

              padding: 16,

              color: '#666',

              background:

                '#fafafa',

            }}

          >

            No clinical narrative

            sections are configured for

            this note.

          </div>

        )}



        {/* ===================================================

            CONTENT SECTIONS

            =================================================== */}

        {rows.length > 0 && (

          <div

            style={{

              display: 'grid',

              gap: 8,

            }}

          >

            {rows.map(

              (row) => {

                const key =

                  getContentKey(

                    row

                  );



                return (

                  <ContentRow

                    key={key}
                      isDailyNote={loadedNoteType === 'DAILY'}

                    row={row}

                    disabled={

                      disabled

                    }

                    saveStatus={

                      rowStatuses[

                        key

                      ] ||

                      'saved'

                    }

                    saveError={

                      rowSaveErrors[

                        key

                      ] ||

                      ''

                    }

                    validationErrors={

                      validationByKey[

                        key

                      ] ||

                      {}

                    }

                    onContentChange={(

                      value

                    ) =>

                      updateContentText(

                        key,

                        value

                      )

                    }

                    onRetry={() =>

                      retryRow(

                        key

                      )

                    }

                  />

                );

              }

            )}

          </div>

        )}



        {disabled && (

          <div

            style={{

              marginTop: 14,

              fontSize: 13,

              color: '#666',

            }}

          >

            Clinical narrative is read

            only because this note is no

            longer in the normal Draft

            editing state.

          </div>

        )}

      </div>

    </section>

  );

}